// @ts-nocheck
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Node, Edge, Connection, addEdge } from 'reactflow';
import { useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getFlow, updateFlow, updateFlowStatus } from '@/services/flowService';
import { Flow, FlowStatus } from '@/types/firestore';
import { validateFlow, ValidationError as ValidationResult } from '@/utils/flowValidation';
import type { ValidationIssue } from '@/components/flow-editor/ValidationPanel';
import type { PrimitiveCapabilityRequirements } from '@simcheck/shared-types';
import { toDate } from '@/lib/date-utils';
import type { CodeError } from '@/components/flow-editor/CodeView';

export type EditorView = 'visual' | 'code';

interface FlowEditorState {
  nodes: Node[];
  edges: Edge[];
  flowName: string;
  flowDescription: string;
  flowStatus: FlowStatus;
  capabilityRequirements: PrimitiveCapabilityRequirements;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  canUndo: boolean;
  canRedo: boolean;
  validationIssues: ValidationIssue[];
  hasErrors: boolean;
  // Code view state
  activeView: EditorView;
  codeContent: string;
  codeErrors: CodeError[];
}

interface FlowEditorContextType extends FlowEditorState {
  // Node operations
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void;
  updateNodeData: (nodeId: string, data: any) => void;
  onNodesChange: (changes: any) => void;
  
  // Edge operations
  setEdges: (edges: Edge[] | ((edges: Edge[]) => Edge[])) => void;
  onEdgesChange: (changes: any) => void;
  onConnect: (connection: Connection) => void;
  
  // Flow metadata
  setFlowName: (name: string) => void;
  setFlowDescription: (description: string) => void;
  setFlowStatus: (status: FlowStatus) => Promise<void>;
  setCapabilityRequirements: (requirements: PrimitiveCapabilityRequirements) => void;
  
  // History operations
  undo: () => void;
  redo: () => void;
  
  // Save operations
  save: () => Promise<void>;
  enableAutoSave: (enabled: boolean) => void;
  
  // Load operation
  loadFlow: (flowId: string) => Promise<void>;

  // Validation
  runValidation: () => void;

  // Code view operations
  setActiveView: (view: EditorView) => void;
  updateCodeContent: (code: string) => void;
  syncVisualToCode: () => void;
  syncCodeToVisual: () => boolean;
}

const FlowEditorContext = createContext<FlowEditorContextType | null>(null);

const MAX_HISTORY = 50;
const AUTO_SAVE_DELAY = 3000; // 3 seconds

interface HistoryEntry {
  nodes: Node[];
  edges: Edge[];
}

export function FlowEditorProvider({ 
  children, 
  isPrimitive = false 
}: { 
  children: React.ReactNode;
  isPrimitive?: boolean;
}) {
  const { activeOrganization } = useAuth();
  const { flowId, primitiveId } = useParams<{ flowId: string; primitiveId: string }>();
  const editorId = isPrimitive ? primitiveId : flowId;
  
  // Core state
  const [nodes, setNodesState] = useState<Node[]>([]);
  const [edges, setEdgesState] = useState<Edge[]>([]);
  const [flowName, setFlowNameState] = useState(isPrimitive ? 'Untitled Primitive' : 'Untitled Flow');
  const [flowDescription, setFlowDescriptionState] = useState('');
  const [flowStatus, setFlowStatusState] = useState<FlowStatus>('draft');
  const [capabilityRequirements, setCapabilityRequirementsState] = useState<PrimitiveCapabilityRequirements>({
    required: [],
    preferred: [],
    modemCount: 1,
  });
  
  // Editor state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  
  // History for undo/redo
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isApplyingHistory = useRef(false);
  
  // Auto-save
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const autoSaveTimeout = useRef<NodeJS.Timeout>();
  
  // Validation
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const [hasErrors, setHasErrors] = useState(false);

  // Code view state
  const [activeView, setActiveViewState] = useState<EditorView>('visual');
  const [codeContent, setCodeContent] = useState('');
  const [codeErrors, setCodeErrors] = useState<CodeError[]>([]);
  
  // Add to history. Dedups no-op snapshots (e.g. duplicate fire after loadFlow
  // initializes history) so undo doesn't appear to do nothing.
  const addToHistory = useCallback(() => {
    if (isApplyingHistory.current) return;

    const nodesJson = JSON.stringify(nodes);
    const edgesJson = JSON.stringify(edges);
    const current = history[historyIndex];
    if (
      current &&
      JSON.stringify(current.nodes) === nodesJson &&
      JSON.stringify(current.edges) === edgesJson
    ) {
      return;
    }

    const newEntry: HistoryEntry = {
      nodes: JSON.parse(nodesJson),
      edges: JSON.parse(edgesJson),
    };

    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newEntry);
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });

    setHistoryIndex((prev) => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [nodes, edges, historyIndex, history]);
  
  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      isApplyingHistory.current = true;
      const prevEntry = history[historyIndex - 1];
      setNodesState(prevEntry.nodes);
      setEdgesState(prevEntry.edges);
      setHistoryIndex(historyIndex - 1);
      setHasUnsavedChanges(true);
      setTimeout(() => {
        isApplyingHistory.current = false;
      }, 0);
    }
  }, [history, historyIndex]);
  
  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isApplyingHistory.current = true;
      const nextEntry = history[historyIndex + 1];
      setNodesState(nextEntry.nodes);
      setEdgesState(nextEntry.edges);
      setHistoryIndex(historyIndex + 1);
      setHasUnsavedChanges(true);
      setTimeout(() => {
        isApplyingHistory.current = false;
      }, 0);
    }
  }, [history, historyIndex]);
  
  // Detect whether two node arrays differ in author-meaningful ways. ReactFlow
  // fires onNodesChange for selection clicks and dimension measurements on
  // mount — neither is a real edit, but both used to flip hasUnsavedChanges,
  // making the leave-prompt appear after just clicking around.
  const nodesAreMeaningfullyEqual = (a: Node[], b: Node[]): boolean => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const p = a[i];
      const n = b[i];
      if (p.id !== n.id || p.type !== n.type) return false;
      if (p.position.x !== n.position.x || p.position.y !== n.position.y) return false;
      if (p.parentNode !== n.parentNode) return false;
      if (JSON.stringify(p.data) !== JSON.stringify(n.data)) return false;
      // Ignore: selected, dragging, width, height, positionAbsolute, zIndex.
    }
    return true;
  };

  const edgesAreMeaningfullyEqual = (a: Edge[], b: Edge[]): boolean => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const p = a[i];
      const n = b[i];
      if (p.id !== n.id) return false;
      if (p.source !== n.source || p.target !== n.target) return false;
      if (p.sourceHandle !== n.sourceHandle || p.targetHandle !== n.targetHandle) return false;
      if (p.type !== n.type) return false;
      if (JSON.stringify(p.data) !== JSON.stringify(n.data)) return false;
      // Ignore: selected, animated, style.
    }
    return true;
  };

  const setNodes = useCallback((nodesOrUpdater: Node[] | ((nodes: Node[]) => Node[])) => {
    setNodesState((prev) => {
      const newNodes = typeof nodesOrUpdater === 'function' ? nodesOrUpdater(prev) : nodesOrUpdater;
      if (!nodesAreMeaningfullyEqual(prev, newNodes)) {
        setHasUnsavedChanges(true);
      }
      return newNodes;
    });
  }, []);

  const setEdges = useCallback((edgesOrUpdater: Edge[] | ((edges: Edge[]) => Edge[])) => {
    setEdgesState((prev) => {
      const newEdges = typeof edgesOrUpdater === 'function' ? edgesOrUpdater(prev) : edgesOrUpdater;
      if (!edgesAreMeaningfullyEqual(prev, newEdges)) {
        setHasUnsavedChanges(true);
      }
      return newEdges;
    });
  }, []);
  
  // Update node data
  const updateNodeData = useCallback((nodeId: string, data: any) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
      )
    );
  }, [setNodes]);
  
  // ReactFlow handlers
  const onNodesChange = useCallback((changes: any) => {
    setNodesState((nds) => {
      // Apply changes (ReactFlow will handle this)
      const updatedNodes = nds; // Simplified - ReactFlow provides applyNodeChanges
      setHasUnsavedChanges(true);
      return updatedNodes;
    });
  }, []);
  
  const onEdgesChange = useCallback((changes: any) => {
    setEdgesState((eds) => {
      // Apply changes (ReactFlow will handle this)
      const updatedEdges = eds; // Simplified - ReactFlow provides applyEdgeChanges
      setHasUnsavedChanges(true);
      return updatedEdges;
    });
  }, []);
  
  const onConnect = useCallback((connection: Connection) => {
    setEdges((eds) => addEdge(connection, eds));
  }, [setEdges]);
  
  // Load flow or primitive from Firestore
  const loadFlow = useCallback(async (id: string) => {
    // For primitives, we don't need organization (they're system-wide)
    if (!isPrimitive && !activeOrganization) return;
    
    try {
      let data;
      
      if (isPrimitive) {
        // Load from primitives collection
        const { getPrimitive } = await import('@/services/primitiveService');
        data = await getPrimitive(id);
      } else {
        // Load from flows collection
        data = await getFlow(id, activeOrganization.id);
      }
      
      if (data) {
        setFlowNameState(data.name);
        setFlowDescriptionState(data.description || '');
        setFlowStatusState((data.status as FlowStatus) || 'draft');

        // Flow structure: nodes/edges can be either at top level OR nested under 'flow'
        // API creates flows with flow: { nodes, edges }, but older flows may have nodes/edges at top level
        const flowNodes = data.flow?.nodes || data.nodes || [];
        const flowEdges = data.flow?.edges || data.edges || [];

        setNodesState(flowNodes);
        setEdgesState(flowEdges);

        // Load capability requirements for primitives
        if (isPrimitive && data.capabilityRequirements) {
          setCapabilityRequirementsState(data.capabilityRequirements);
        }

        setHasUnsavedChanges(false);
        setLastSaved(toDate(data.updatedAt) || null);

        // Initialize history
        setHistory([{
          nodes: flowNodes,
          edges: flowEdges,
        }]);
        setHistoryIndex(0);
      }
    } catch (error) {
      console.error(`Failed to load ${isPrimitive ? 'primitive' : 'flow'}:`, error);
    }
  }, [activeOrganization, isPrimitive]);
  
  // Run validation using new comprehensive validation system
  const runValidation = useCallback(() => {
    // Use new validation that enforces @simcheck/shared-types schema
    const result = validateFlow({ nodes, edges });

    // Helper to determine category from error message/field
    const getCategory = (message: string, field?: string): 'structure' | 'configuration' | 'logic' | 'performance' => {
      const msg = message.toLowerCase();
      // Structure: missing nodes, invalid connections, missing required elements
      if (msg.includes('start node') || msg.includes('end node') || msg.includes('reachable') ||
          msg.includes('connected') || msg.includes('must have') || msg.includes('not found') ||
          msg.includes('empty data') || field === 'type' || field === 'id') {
        return 'structure';
      }
      // Configuration: missing or invalid field values
      if (msg.includes('must have a') || msg.includes('missing') || msg.includes('should have') ||
          field?.includes('data.') || msg.includes('invalid') || msg.includes('must be')) {
        return 'configuration';
      }
      // Logic: expression/condition issues
      if (msg.includes('expression') || msg.includes('condition') || msg.includes('assertion')) {
        return 'logic';
      }
      // Default to configuration for most field-level issues
      return 'configuration';
    };

    // Convert validation results to ValidationIssue format for UI
    const issues: ValidationIssue[] = [
      ...result.errors.map((error: ValidationResult) => ({
        id: error.nodeId || error.edgeId || Math.random().toString(),
        nodeId: error.nodeId,
        edgeId: error.edgeId,
        severity: 'error' as const,
        message: error.message,
        field: error.field,
        category: getCategory(error.message, error.field),
      })),
      ...result.warnings.map((warning: ValidationResult) => ({
        id: warning.nodeId || warning.edgeId || Math.random().toString(),
        nodeId: warning.nodeId,
        edgeId: warning.edgeId,
        severity: 'warning' as const,
        message: warning.message,
        field: warning.field,
        category: getCategory(warning.message, warning.field),
      })),
    ];

    setValidationIssues(issues);
    setHasErrors(!result.valid);
  }, [nodes, edges]);
  
  // Save flow or primitive to Firestore
  const save = useCallback(async () => {
    if (!isPrimitive && !activeOrganization) return;
    if (!editorId) return;
    
    // Validation: Regular flows (scenarios) cannot contain AT command nodes
    if (!isPrimitive) {
      const hasAtCommandNodes = nodes.some(node => node.type === 'atCommand');
      if (hasAtCommandNodes) {
        throw new Error(
          'Regular flows cannot contain AT Command nodes. ' +
          'AT Commands can only be used in Primitives. ' +
          'Please use Primitive Call nodes to invoke primitives instead.'
        );
      }
    }
    
    setIsSaving(true);
    try {
      if (isPrimitive) {
        // Save to primitives collection
        const { updatePrimitive } = await import('@/services/primitiveService');
        await updatePrimitive(editorId, {
          name: flowName,
          description: flowDescription,
          flow: {
            nodes,
            edges,
          },
          capabilityRequirements,
        });
      } else {
        // Save to flows collection using canonical nested structure
        await updateFlow(editorId, activeOrganization.id, {
          name: flowName,
          description: flowDescription,
          flow: {
            nodes,
            edges,
          },
        });
      }
      
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error(`Failed to save ${isPrimitive ? 'primitive' : 'flow'}:`, error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [activeOrganization, editorId, flowName, flowDescription, nodes, edges, capabilityRequirements, isPrimitive]);

  // Capability requirements setter that marks as unsaved
  const setCapabilityRequirements = useCallback((requirements: PrimitiveCapabilityRequirements) => {
    setCapabilityRequirementsState(requirements);
    setHasUnsavedChanges(true);
  }, []);

  // Name / description setters that mark as unsaved (raw *State setters are used
  // for the load path and code-view sync, which manage the dirty flag themselves).
  const setFlowName = useCallback((name: string) => {
    setFlowNameState(name);
    setHasUnsavedChanges(true);
  }, []);

  const setFlowDescription = useCallback((description: string) => {
    setFlowDescriptionState(description);
    setHasUnsavedChanges(true);
  }, []);

  // Flow status (draft ⇄ published) is a lightweight lifecycle field, not part
  // of the flow graph — it writes to Firestore immediately (does not ride the
  // graph Save / dirty flag). No-op for primitives (they use their own
  // isPublished model in the primitives collection).
  const setFlowStatus = useCallback(async (status: FlowStatus) => {
    if (isPrimitive || !editorId || !activeOrganization) return;
    await updateFlowStatus(editorId, activeOrganization.id, status);
    setFlowStatusState(status);
  }, [isPrimitive, editorId, activeOrganization]);

  // Sync visual state to code (serialize nodes/edges to JSON)
  // CANONICAL: Always use nested flow structure { flow: { nodes, edges } }
  const syncVisualToCode = useCallback(() => {
    const flowData = isPrimitive
      ? {
          name: flowName,
          description: flowDescription,
          flow: { nodes, edges },
          capabilityRequirements,
        }
      : {
          name: flowName,
          description: flowDescription,
          flow: { nodes, edges },
        };

    setCodeContent(JSON.stringify(flowData, null, 2));
    setCodeErrors([]);
  }, [isPrimitive, flowName, flowDescription, nodes, edges, capabilityRequirements]);

  // Sync code to visual state (parse JSON, validate, update nodes/edges)
  const syncCodeToVisual = useCallback((): boolean => {
    if (!codeContent.trim()) {
      setCodeErrors([{ line: 1, column: 1, message: 'Empty content', severity: 'error' }]);
      return false;
    }

    try {
      const parsed = JSON.parse(codeContent);

      // Determine structure based on primitive vs regular flow
      let newNodes: Node[];
      let newEdges: Edge[];
      let newName: string;
      let newDescription: string;

      // CANONICAL: Both primitives and regular flows use nested structure
      // { name, description, flow: { nodes, edges } }
      // Support legacy top-level format for backwards compatibility when reading
      newNodes = parsed.flow?.nodes || parsed.nodes || [];
      newEdges = parsed.flow?.edges || parsed.edges || [];
      newName = parsed.name || flowName;
      newDescription = parsed.description || '';

      // Update capability requirements if present (primitives only)
      if (isPrimitive && parsed.capabilityRequirements) {
        setCapabilityRequirementsState(parsed.capabilityRequirements);
      }

      // Validate basic structure
      if (!Array.isArray(newNodes)) {
        setCodeErrors([{
          line: 1,
          column: 1,
          message: 'nodes must be an array',
          severity: 'error'
        }]);
        return false;
      }

      if (!Array.isArray(newEdges)) {
        setCodeErrors([{
          line: 1,
          column: 1,
          message: 'edges must be an array',
          severity: 'error'
        }]);
        return false;
      }

      // Validate each node has required fields
      for (let i = 0; i < newNodes.length; i++) {
        const node = newNodes[i];
        if (!node.id) {
          setCodeErrors([{
            line: 1,
            column: 1,
            message: `Node at index ${i} is missing 'id' field`,
            severity: 'error'
          }]);
          return false;
        }
        if (!node.type) {
          setCodeErrors([{
            line: 1,
            column: 1,
            message: `Node "${node.id}" is missing 'type' field`,
            severity: 'error'
          }]);
          return false;
        }
        if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
          setCodeErrors([{
            line: 1,
            column: 1,
            message: `Node "${node.id}" is missing valid 'position' field`,
            severity: 'error'
          }]);
          return false;
        }
        // Ensure data object exists
        if (!node.data) {
          newNodes[i] = { ...node, data: {} };
        }
      }

      // Validate each edge has required fields
      for (let i = 0; i < newEdges.length; i++) {
        const edge = newEdges[i];
        if (!edge.id) {
          setCodeErrors([{
            line: 1,
            column: 1,
            message: `Edge at index ${i} is missing 'id' field`,
            severity: 'error'
          }]);
          return false;
        }
        if (!edge.source || !edge.target) {
          setCodeErrors([{
            line: 1,
            column: 1,
            message: `Edge "${edge.id}" is missing 'source' or 'target' field`,
            severity: 'error'
          }]);
          return false;
        }
      }

      // All valid - update state
      setNodesState(newNodes);
      setEdgesState(newEdges);
      setFlowNameState(newName);
      setFlowDescriptionState(newDescription);
      setCodeErrors([]);
      setHasUnsavedChanges(true);

      return true;
    } catch (e) {
      // JSON parse error
      const error = e as SyntaxError;
      // Try to extract line number from error message
      const lineMatch = error.message.match(/position (\d+)/);
      const position = lineMatch ? parseInt(lineMatch[1], 10) : 0;

      // Estimate line number from position
      const lines = codeContent.substring(0, position).split('\n');
      const line = lines.length;
      const column = lines[lines.length - 1]?.length || 1;

      setCodeErrors([{
        line,
        column,
        message: `Invalid JSON: ${error.message}`,
        severity: 'error'
      }]);
      return false;
    }
  }, [codeContent, isPrimitive, flowName, flowDescription]);

  // Update code content and try to sync
  const updateCodeContent = useCallback((code: string) => {
    setCodeContent(code);

    // Try to parse and validate in real-time
    try {
      JSON.parse(code);
      // Valid JSON - will be synced when switching views
    } catch (e) {
      // Don't set errors on every keystroke - Monaco handles that
    }
  }, []);

  // Set active view with sync handling
  const setActiveView = useCallback((view: EditorView) => {
    if (view === activeView) return;

    if (view === 'code') {
      // Switching to code view - sync visual to code
      syncVisualToCode();
    } else {
      // Switching to visual view - try to sync code to visual
      if (codeErrors.length === 0) {
        syncCodeToVisual();
      }
    }

    setActiveViewState(view);
  }, [activeView, syncVisualToCode, syncCodeToVisual, codeErrors]);
  
  // Autosave disabled — user must hit the Save button. The previous 3s
  // autosave overwrote the canonical flow doc on every keystroke, which
  // (a) made "undo what I just did" useless after a reload and
  // (b) is dangerous for global primitives (one typo propagates fleet-wide
  //     within 3s with no recall).
  // Tracked: see issue for draft/publish redesign.
  // void AUTO_SAVE_DELAY, autoSaveEnabled, autoSaveTimeout — kept in scope
  // for the eventual draft/publish reintroduction.

  // Warn before closing/reloading the tab with unsaved changes so they're
  // not silently lost.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore the custom message but the prompt still fires.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);
  
  // Load flow or primitive on mount
  useEffect(() => {
    if (editorId) {
      loadFlow(editorId);
    }
  }, [editorId, loadFlow]);
  
  // Capture history snapshots after change activity quiets — keeps a node
  // drag (which fires onNodesChange on every tick) from polluting history
  // with ~50 micro-entries. 250ms is short enough to feel responsive.
  useEffect(() => {
    if (nodes.length === 0 && edges.length === 0) return;
    const timer = setTimeout(() => {
      addToHistory();
    }, 250);
    return () => clearTimeout(timer);
  }, [nodes, edges]); // addToHistory intentionally omitted to avoid loop
  
  // Run validation when nodes/edges change
  useEffect(() => {
    runValidation();
  }, [nodes, edges, runValidation]);
  
  const value: FlowEditorContextType = {
    // State
    nodes,
    edges,
    flowName,
    flowDescription,
    flowStatus,
    capabilityRequirements,
    hasUnsavedChanges,
    isSaving,
    lastSaved,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    validationIssues,
    hasErrors,
    // Code view state
    activeView,
    codeContent,
    codeErrors,

    // Operations
    setNodes,
    updateNodeData,
    onNodesChange,
    setEdges,
    onEdgesChange,
    onConnect,
    setFlowName,
    setFlowDescription,
    setFlowStatus,
    setCapabilityRequirements,
    undo,
    redo,
    save,
    enableAutoSave: setAutoSaveEnabled,
    loadFlow,
    runValidation,
    // Code view operations
    setActiveView,
    updateCodeContent,
    syncVisualToCode,
    syncCodeToVisual,
  };
  
  return (
    <FlowEditorContext.Provider value={value}>
      {children}
    </FlowEditorContext.Provider>
  );
}

export function useFlowEditor() {
  const context = useContext(FlowEditorContext);
  if (!context) {
    throw new Error('useFlowEditor must be used within FlowEditorProvider');
  }
  return context;
}

